// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// interfaces
import { PaymentMethodProps, InAppUpiPaymentMethod, IInAppUpiPaymentGroup } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodListView as InAppUpiListView } from "@payment-ui/widgets/payment-method-views/list-view";

// helpers
import { canEnablePaymentMethod, getPaymentMethodDisplayName } from "@payment-ui/helpers/Payment";
import { getPayCtaMsg } from "@payment-ui/helpers/InAppUpi";

// test-ids
import { INAPP_UPI_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

export interface InAppUpiProps extends PaymentMethodProps {
    methodData: InAppUpiPaymentMethod;
    paymentActions: IInAppUpiPaymentGroup["paymentActions"];
    paymentAmount: string;
    paymentAmountWithoutDiscount?: string;
    onClickPaymentGroups?: (context: string, label: string, value: string) => void;
    position?: number;
}

function InAppUPIPaymentMethod(props: InAppUpiProps): ReactElement {
    const {
        methodData,
        paymentActions,
        isPaymentGroupEnabled,
        selected,
        paymentAmount,
        onClickPaymentGroups,
        position,
    } = props;

    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(methodData, isPaymentGroupEnabled), [
        isPaymentGroupEnabled,
        methodData,
    ]);

    const liteBalance = useMemo(() => methodData?.meta?.liteBalance, [methodData]);
    const payCtaMsg = useMemo(() => getPayCtaMsg(methodData, paymentAmount), [methodData, paymentAmount]);

    const onPay = useCallback(
        (extraData = {}): void => {
            if (!isMethodEnabled) {
                return;
            }
            paymentActions.onPay(
                props.methodData,
                {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    in_app_upi_params: {
                        account_reference_id: props?.methodData?.meta?.referenceId || "",
                        account_type: props?.methodData?.meta?.accountType || "",
                    },
                    /* eslint-enable */
                },
                extraData,
            );
        },
        [isMethodEnabled, paymentActions, props.methodData],
    );

    const onClick = useCallback((): void => {
        onClickPaymentGroups &&
            onClickPaymentGroups(
                getPaymentMethodDisplayName(props.methodData),
                String(isMethodEnabled),
                String(position),
            );

        if (!isMethodEnabled) {
            return;
        }
        paymentActions.onSelect(props.methodData);
    }, [isMethodEnabled, paymentActions, props.methodData, onClickPaymentGroups, position]);

    return (
        <InAppUpiListView
            methodData={props.methodData}
            isMethodEnabled={isMethodEnabled}
            onClick={onClick}
            balance={liteBalance}
            onPayButtonClick={onPay}
            testIds={INAPP_UPI_PAYMENT_METHOD_TEST_IDS}
            selected={selected}
            payCtaMsg={payCtaMsg}
        />
    );
}

export default memo(InAppUPIPaymentMethod);
